import { nameAccessorials } from "@/Data/nameAccessorials";
import { describeLocationType } from "@/Data/describeLocationType";
import DeliveryDeadlineQuestionBubble from "@/Shared/Locations/Bubbles/DeliveryDeadlineQuestionBubble";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { quotesFormatLocation } from "../Helpers/quotesFormatLocation";
import type { QuoteLike } from "@/Shared/Quotes/Types/QuoteLikeType";
import { formatPhone } from "@/Helpers/formatPhone";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import moment from "moment";
import QuotesEstimatedEarliestDeliveryDateQuestionBubble from "../Bubbles/QuotesEstimatedEarliestDeliveryDateQuestionBubble";
import QuotesPredictedLatestDeliveryDateQuestionBubble from "../Bubbles/QuotesPredictedLatestDeliveryDateQuestionBubble";
import { QuotesColumn } from "@/Shared/Quotes/CustomComponents/QuotesColumn";
import { QuotesColumnHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnHeader";
import { QuotesColumnSectionHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnSectionHeader";
import { QuotesTextLink } from "@/Shared/Quotes/CustomComponents/QuotesTextLink";
import { QuotesLocationTypeDescription } from "@/Shared/Quotes/CustomComponents/QuotesLocationTypeDescription";
import { QuotesModifyLink } from "@/Shared/Quotes/CustomComponents/QuotesModifyLink";
import { QuotesNotesSection } from "@/Shared/Quotes/CustomComponents/QuotesNotesSection";
import { QuotesPrimaryText } from "@/Shared/Quotes/CustomComponents/QuotesPrimaryText";
import { QuotesReferenceNumber } from "@/Shared/Quotes/CustomComponents/QuotesReferenceNumber";
import { QuotesSecondaryText } from "@/Shared/Quotes/CustomComponents/QuotesSecondaryText";
import { bookShipmentIsWarehouseNotRequiringContact } from "@/Shared/Locations/Helpers/isWarehouseNotRequiringContact";
import { shipmentIsDeliveringByAppointment } from "@/Shared/Shipment/Helpers/shipmentIsDeliveringByAppointment";
import {
  describeLoadingCapabilities,
  hasLiftGateAccessorial,
} from "@/Shared/Locations/Helpers/loadingCapabilities";

interface DeliveryColumnProps {
  deliveryHours: string | undefined;
  /** Set once the carrier has booked the delivery appointment. */
  appointmentDate: string | undefined;
  deliveryLocation: Location | AddLocationLocationType | PreQuoteLocation;
  deliveryContact: Contact | undefined;
  quote: QuoteLike | undefined;
  shipmentState: ShipmentState;
  actualDeliveryDate: string | undefined;
  actualDeliveryTime: string | undefined;
  expectedDeliveryDate: string | undefined;
  latestExpectedDeliveryDate: string | undefined;
  deliveryDeadline: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  // Unpacked server-side from the reference for Amazon warehouse deliveries
  deliveryAmazonReferenceId?: string | undefined;
  deliveryAmazonShipmentId?: string | undefined;
  deliveryBoothNumber?: string | undefined;
  onModifyDetails?: (() => void) | undefined;
  onModifyDeliveryAddress?: (() => void) | undefined;
  onModifyDeliveryContact?: (() => void) | undefined;
  onModifyDeliveryReferenceAndNotes?: (() => void) | undefined;
  onChangeDeliveryHours?: (() => void) | undefined;
  showNotesSection: boolean;
  // Is this in a two column layout?
  twoColumn?: boolean | undefined;
  hideExpectedDelivery?: boolean | undefined;
  deliveryLocationHasDock?: LoadingCapabilitiesAvailability | undefined;
  deliveryLocationHasForklift?: LoadingCapabilitiesAvailability | undefined;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
function WhenSection_(props: DeliveryColumnProps) {
  const { deliveryHours, shipmentState } = props;

  if (
    shipmentState === ShipmentState.Lost ||
    shipmentState === ShipmentState.Cancelled
  ) {
    return <></>;
  }

  const onHold = props.shipmentState === ShipmentState.OnHold;

  if (onHold) {
    return (
      <>
        <QuotesColumnSectionHeader>When</QuotesColumnSectionHeader>
        <QuotesPrimaryText>On Hold</QuotesPrimaryText>
      </>
    );
  } else if (shipmentState === ShipmentState.Delivered) {
    const formattedDate = moment(props.actualDeliveryDate).format(
      "dddd Do MMMM YYYY",
    );

    const formattedTime = props.actualDeliveryTime
      ? moment(props.actualDeliveryTime, "HH:mm").format("h:mma")
      : undefined;

    return (
      <>
        <QuotesColumnSectionHeader>When</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{formattedDate}</QuotesPrimaryText>
        {formattedTime && (
          <QuotesSecondaryText>
            Delivered at: {formattedTime}
          </QuotesSecondaryText>
        )}
      </>
    );
  } else {
    const formattedDate =
      props.expectedDeliveryDate !== undefined
        ? moment(props.expectedDeliveryDate).format("dddd Do MMMM YYYY")
        : "Unknown Delivery Date";

    const showPredictedLatestDate =
      props.latestExpectedDeliveryDate &&
      moment(props.expectedDeliveryDate).isBefore(
        moment(props.latestExpectedDeliveryDate),
      );

    const showDeliveryDeadline = props.deliveryDeadline !== undefined;

    const formattedPredictedLatestDate = props.latestExpectedDeliveryDate
      ? moment(props.latestExpectedDeliveryDate).format("dddd Do MMMM YYYY")
      : "";

    const formattedDeliveryDeadline = props.deliveryDeadline
      ? moment(props.deliveryDeadline).format("dddd Do MMMM YYYY")
      : "";

    return (
      <>
        {!props.hideExpectedDelivery && (
          <>
            <QuotesColumnSectionHeader>
              Earliest Delivery
              <QuotesEstimatedEarliestDeliveryDateQuestionBubble />
            </QuotesColumnSectionHeader>
            <QuotesPrimaryText>{formattedDate}</QuotesPrimaryText>
          </>
        )}
        {deliveryHours && (
          <QuotesSecondaryText>
            {/* The carrier sets an appointment delivery's time with the
             * consignee, so the location's hours would mislead here. */}
            {shipmentIsDeliveringByAppointment(props)
              ? "Delivery appointment required"
              : `Delivery window: ${deliveryHours}`}
          </QuotesSecondaryText>
        )}
        {props.onChangeDeliveryHours && (
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onChangeDeliveryHours}>
              Change delivery hours
            </QuotesTextLink>
          </QuotesModifyLink>
        )}
        {showPredictedLatestDate && (
          <>
            <QuotesColumnSectionHeader>
              Latest Delivery
              <QuotesPredictedLatestDeliveryDateQuestionBubble />
            </QuotesColumnSectionHeader>
            <QuotesPrimaryText>
              {formattedPredictedLatestDate}
            </QuotesPrimaryText>
          </>
        )}
        {showDeliveryDeadline && (
          <>
            <QuotesColumnSectionHeader>
              Delivery Deadline
              <DeliveryDeadlineQuestionBubble />
            </QuotesColumnSectionHeader>
            <QuotesPrimaryText>{formattedDeliveryDeadline}</QuotesPrimaryText>
          </>
        )}
      </>
    );
  }
}

function WhereSection_(props: DeliveryColumnProps) {
  const { deliveryLocation } = props;

  const locationTypeDescription = describeLocationType(
    deliveryLocation.locationType,
    deliveryLocation.distributionWarehouseBrand,
  );
  const formattedLocation = quotesFormatLocation(deliveryLocation);

  const isAmazonReference =
    props.deliveryAmazonReferenceId !== undefined ||
    props.deliveryAmazonShipmentId !== undefined;

  if (deliveryLocation.businessName === undefined) {
    return (
      <>
        <QuotesColumnSectionHeader>Where</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{formattedLocation}</QuotesPrimaryText>
        <QuotesSecondaryText>{locationTypeDescription}</QuotesSecondaryText>
      </>
    );
  } else {
    return (
      <>
        <QuotesColumnSectionHeader>Where</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{deliveryLocation.businessName}</QuotesPrimaryText>
        {props.deliveryReferenceNumber && (
          <QuotesReferenceNumber>
            {isAmazonReference ? (
              <div className="flex flex-col flex-nowrap items-start">
                {props.deliveryAmazonReferenceId && (
                  <div>Amazon Ref ID: {props.deliveryAmazonReferenceId}</div>
                )}
                {props.deliveryAmazonShipmentId && (
                  <div>
                    Amazon Shipment ID: {props.deliveryAmazonShipmentId}
                  </div>
                )}
              </div>
            ) : (
              <div>Reference: {props.deliveryReferenceNumber}</div>
            )}
          </QuotesReferenceNumber>
        )}
        <QuotesSecondaryText>
          Location Type:{" "}
          <QuotesLocationTypeDescription>
            {locationTypeDescription}
          </QuotesLocationTypeDescription>
        </QuotesSecondaryText>
        <QuotesSecondaryText>
          {deliveryLocation.address.addressLine}
        </QuotesSecondaryText>
        <QuotesSecondaryText>
          {deliveryLocation.address.addressLine2}
        </QuotesSecondaryText>
        <QuotesSecondaryText>{formattedLocation}</QuotesSecondaryText>
        {props.onModifyDeliveryAddress && (
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onModifyDeliveryAddress}>
              Change address
            </QuotesTextLink>
          </QuotesModifyLink>
        )}
      </>
    );
  }
}

function ContactSection_(props: DeliveryColumnProps) {
  const { deliveryContact } = props;

  if (deliveryContact?.contactName === undefined) {
    return <></>;
  } else {
    return (
      <>
        <QuotesColumnSectionHeader>Contact</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{deliveryContact?.contactName}</QuotesPrimaryText>
        <QuotesSecondaryText>
          {deliveryContact?.emailAddress}
        </QuotesSecondaryText>
        <QuotesSecondaryText>
          {formatPhone(
            deliveryContact.phoneNumber,
            deliveryContact.phoneNumberExtension,
          )}
        </QuotesSecondaryText>
        {props.onModifyDeliveryContact && (
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onModifyDeliveryContact}>
              Change contact
            </QuotesTextLink>
          </QuotesModifyLink>
        )}
      </>
    );
  }
}

function SpecialServicesSection_(props: DeliveryColumnProps) {
  const { deliveryLocation } = props;

  const lines = nameAccessorials(
    deliveryLocation.accessorials,
    LocationContext.Delivery,
    deliveryLocation.locationType,
  );

  return (
    <>
      <QuotesColumnSectionHeader>Special Services</QuotesColumnSectionHeader>
      {/* Text styles live on the li (not a child div) so the bullet marker
       * matches the text's size and colour instead of floating at the
       * inherited 16px black. */}
      <ul className="mb-0 ps-5">
        {lines.map((line) => {
          return (
            <li
              key={line}
              className="text-[14px] font-light text-content-muted"
            >
              {line}
            </li>
          );
        })}
      </ul>
      {props.onModifyDetails && (
        <>
          <div className="h-2" />
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onModifyDetails}>
              Modify special services
            </QuotesTextLink>
          </QuotesModifyLink>
        </>
      )}
    </>
  );
}

function LoadingCapabilitiesSection_(props: DeliveryColumnProps) {
  if (
    props.deliveryLocationHasDock === undefined &&
    props.deliveryLocationHasForklift === undefined
  ) {
    return <></>;
  }

  const locationHasDock =
    props.deliveryLocationHasDock ?? LoadingCapabilitiesAvailability.Unknown;
  const locationHasForklift =
    props.deliveryLocationHasForklift ??
    LoadingCapabilitiesAvailability.Unknown;

  const description = describeLoadingCapabilities(
    locationHasDock,
    locationHasForklift,
  );

  return (
    <>
      <QuotesColumnSectionHeader>Loading</QuotesColumnSectionHeader>
      <QuotesSecondaryText>{description}</QuotesSecondaryText>
    </>
  );
}

function deliveryLabelForShipmentState(shipmentState: ShipmentState) {
  const deliveryLabel = "Delivery";

  switch (shipmentState) {
    case ShipmentState.QuoteRequested:
    case ShipmentState.Quoted:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingConfirmed:
    case ShipmentState.BookingFailed:
    case ShipmentState.OnHold:
    case ShipmentState.InTransit:
      return deliveryLabel;
    case ShipmentState.Delivered:
      return "Delivered";
    case ShipmentState.Cancelled:
      return "Delivery (Cancelled)";
    case ShipmentState.Lost:
      return "Delivery (Lost)";
  }
}

function QuotesDeliveryColumn(props: DeliveryColumnProps) {
  const label = deliveryLabelForShipmentState(props.shipmentState);

  const isContactNotRequired = bookShipmentIsWarehouseNotRequiringContact(
    props.deliveryLocation.distributionWarehouseBrand,
  );

  return (
    <QuotesColumn twoColumn={props.twoColumn}>
      <div className="flex flex-col flex-nowrap items-start">
        <QuotesColumnHeader>{label}</QuotesColumnHeader>
        <WhenSection_ {...props} />
        <WhereSection_ {...props} />
        {!isContactNotRequired && <ContactSection_ {...props} />}
        {props.showNotesSection && (
          <QuotesNotesSection
            reference={props.deliveryReferenceNumber}
            amazonReferenceId={props.deliveryAmazonReferenceId}
            amazonShipmentId={props.deliveryAmazonShipmentId}
            boothNumber={props.deliveryBoothNumber}
            notes={props.deliveryLocation.notes}
            locationType={props.deliveryLocation.locationType}
            onModify={props.onModifyDeliveryReferenceAndNotes}
          />
        )}
        <SpecialServicesSection_ {...props} />
        {!hasLiftGateAccessorial(props.deliveryLocation.accessorials) && (
          <LoadingCapabilitiesSection_ {...props} />
        )}
      </div>
    </QuotesColumn>
  );
}
export default QuotesDeliveryColumn;
